import React from 'react';
import styled from 'styled-components';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import {
  IItemSourceSentenceTranslationValidatorItem,
  IItemSourceSentenceTranslationValidatorItemAiResult,
} from '../../../types/itemSourceSentenceTranslationValidator';

type Props = {
  value?: IItemSourceSentenceTranslationValidatorItemAiResult;
  data?: IItemSourceSentenceTranslationValidatorItem;
};

const ItemSourceSentenceTranslationValidatorAiResultRenderer: React.FC<Props> = (params) => {
  const value = params.value;
  if (!value || typeof value !== 'object') return null;

  const decision = value.decision;
  const overallConfidence = value.confidence;
  const reasoning = value.reasoning;
  const modifiedTranslation = value.modified_translation;
  const errorMessage = value.error;
  const canModify = value.can_modify !== false;
  const cannotModify = decision === 'change' && !canModify;

  const confidencePercent = (n?: number) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '');

  // Decision colors similar to CLValidator style system
  const decisionColor =
    decision === 'keep' ? '#4caf50' : decision === 'change' ? '#1565c0' : decision === 'error' ? '#d32f2f' : '#555';

  // Build decision text
  const decisionLabel =
    decision === 'keep'
      ? 'Keep'
      : decision === 'change'
      ? cannotModify
        ? 'Cannot modify'
        : 'Change'
      : decision === 'error'
      ? 'Error'
      : undefined;


  // Special handling for error decision
  if (decision === 'error') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Error</DecisionText>
        </DecisionHeader>
        {errorMessage && (
          <div style={{ color: '#d32f2f', marginTop: 4 }} title={errorMessage}>
            <i className="fa fa-exclamation-triangle" style={{ marginRight: 4 }} />
            {errorMessage}
          </div>
        )}
      </ResultContainer>
    );
  }

  return (
    <ResultContainer>
      {decisionLabel && (
        <DecisionHeader>
          <DecisionText $color={decisionColor}>{decisionLabel}</DecisionText>
          {typeof overallConfidence === 'number' && (
            <ConfidenceBadge $confidence={overallConfidence}>{confidencePercent(overallConfidence)}</ConfidenceBadge>
          )}
        </DecisionHeader>
      )}

      {cannotModify && (
        <CannotModifyNotice>
          <i className="fa fa-info-circle" /> AI could not produce a safe edit that includes the required item. Please
          modify the sentence manually.
        </CannotModifyNotice>
      )}

      {modifiedTranslation && (
        <>
          {params.data?.original_data?.original_translation ? (
            <DiffContainer>
              <ReactDiffViewer
                oldValue={params.data.original_data.original_translation}
                newValue={modifiedTranslation}
                showDiffOnly
                compareMethod={DiffMethod.WORDS}
                splitView={false}
                hideLineNumbers
                styles={{
                  wordDiff: {
                    display: 'inline',
                  },
                  diffRemoved: {
                    display: 'none',
                  },
                  diffAdded: {
                    display: 'none',
                  },
                  content: {
                    display: 'block!important',
                  },
                  diffContainer: {
                    overflow: 'hidden',
                  },
                  line: {
                    wordBreak: 'break-word',
                  },
                }}
              />
            </DiffContainer>
          ) : (
            <div style={{ color: '#333', marginTop: 4 }}>
              <strong>Modified:</strong> {modifiedTranslation}
            </div>
          )}
        </>
      )}

      {reasoning && (
        <div style={{ color: '#666', lineHeight: 1.4, whiteSpace: 'normal', marginTop: 2 }}>
          {reasoning}
        </div>
      )}
    </ResultContainer>
  );
};

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 1px 0;
`;

const DecisionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
`;

const DecisionText = styled.span<{ $color: string }>`
  color: ${(props) => props.$color};
  font-weight: 600;
  white-space: nowrap;
`;

const ConfidenceBadge = styled.span<{ $confidence: number }>`
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.4;
  background-color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#e8f5e9';
    if (conf >= 0.6) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#2e7d32';
    if (conf >= 0.6) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid
    ${(props) => {
      const conf = props.$confidence;
      if (conf >= 0.8) return '#a5d6a7';
      if (conf >= 0.6) return '#ffcc80';
      return '#ef9a9a';
    }};
`;

const DiffContainer = styled.div`
  margin-top: 4px;
  background-color: #fff;
  padding: 6px;
  border-radius: 2px;
  border: 1px solid #e0e0e0;
`;

const OriginalText = styled.div`
  color: #666;
  margin-top: 4px;
`;

const CannotModifyNotice = styled.div`
  margin-top: 4px;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: #fff3e0;
  color: #bf360c;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;
  i {
    color: #fb8c00;
  }
`;

export default ItemSourceSentenceTranslationValidatorAiResultRenderer;
